import React, { useState, useEffect, useContext } from "react";
import "./payment.css";
import { PopupContext } from "../../../PopupContext";
import { toast } from "react-toastify";
import { ArrowLeft, Copy, Check, ChevronRight, Upload, Send } from "lucide-react";
import axios from "axios";

const Payment = ({
  isOpen = false,
  onClose = () => {},
  packName,
  packType = "local",
}) => {
  const [details, setDetails] = useState(null);
  const userId = localStorage.getItem("userId");
  const userEmail = localStorage.getItem("userEmail");
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const { closeAllPopups } = useContext(PopupContext);

  const [popupState, setPopupState] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedMethodDetails, setSelectedMethodDetails] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [activeTab, setActiveTab] = useState(packType);

  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [receiptPhoto, setReceiptPhoto] = useState(null);
  const [receiptName, setReceiptName] = useState("Uploader le reçu");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // === MÉTHODES DE PAIEMENT ===
  const paymentMethods = {
    local: [
      { value: "poste", label: "Tunisian Post", image: "poste.jpg" },
      { value: "d17", label: "D17", image: "D17.JPG" },
      { value: "izi", label: "IZI", image: "izi.jpeg" },
      { value: "flouci", label: "Flouci", image: "flouci.jpg" },
    ],
    international: [
      { value: "western_Union", label: "Western Union", image: "westernUnion.png" },
      { value: "binance", label: "Binance", image: "binance.png" },
      { value: "bitget", label: "Bitget", image: "Bitget.png" },
      { value: "bingx", label: "BingX", image: "bingx.png" },
    ],
  };

  const methodDetails = {
    poste: { agency: "Poste Tunisienne", number: "5359401735312676", phone: "53314233" },
    d17: { agency: "D17", number: "53314233", phone: "53314233", qrCode: "/images/payment/QR-CodeD17.jpg" },
    izi: { agency: "IZI Pay", number: "81001000014421205555", phone: "53314233", qrCode: "/images/payment/QR-CodeIZI.jpg" },
    flouci: { agency: "Flouci", number: "04050135007891671539", phone: "53314233", qrCode: "/images/payment/flouciQR.jpg" },
    western_Union: { agency: "Western Union", number: "Prénom: XYZ | Nom: ABC", phone: "53314233" },
    binance: { agency: "Binance Pay", number: "USDT TRC20", address: "TAbc123...", beneficiary: "Binance ID: 123456789" },
    bitget: { agency: "Bitget", number: "USDT TRC20", address: "Txyz987...", beneficiary: "UID: 987654" },
    bingx: { agency: "BingX", number: "USDT ERC20", address: "0xabc123...", beneficiary: "UID: 789456" },
  };

  const availableMethods = paymentMethods[activeTab] || paymentMethods.local;

  const formatPrice = (price) =>
    price == null ? "0.000" : Number(price).toFixed(3);

  // === CHARGEMENT ===
  useEffect(() => {
    const fetchPackDetails = async () => {
      if (!packName) return setLoading(false);
      try {
        const res = await axios.get(
          `${API_URL}/packs/${encodeURIComponent(packName)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDetails(res.data);
      } catch (err) {
        setError("Pack non trouvé");
      } finally {
        setLoading(false);
      }
    };
    fetchPackDetails();
  }, [packName, token]);

  useEffect(() => {
    if (details?.price && quantity) {
      setTotalPrice(details.price * quantity);
    }
  }, [details, quantity]);

  // === REÇU ===
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Image requise");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 Mo");
    setReceiptPhoto(file);
    setReceiptName(file.name);
  };

  // === SOUMISSION ===
  const handleSubmit = async () => {
    if (!receiptPhoto) return toast.error("Reçu obligatoire");
    if (!selectedMethod) return toast.error("Choisissez une méthode");
    if (!paymentType) return toast.error("Choisissez le type de paiement");
    if (paymentType === "facility" && !selectedFacility)
      return toast.error("Période requise");

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("userEmail", userEmail);
    formData.append("packName", packName);
    formData.append("price", details.price);
    formData.append("totalPrice", totalPrice);
    formData.append("quantity", quantity);
    formData.append("paymentType", paymentType);
    formData.append("paymentMethod", selectedMethod);
    formData.append("receiptPhoto", receiptPhoto);

    if (paymentType === "facility" && selectedFacility) {
      const facility = details.paymentFacilities.find((f) => f.period === selectedFacility);
      formData.append("paymentFacilities", JSON.stringify(facility));
    }

    try {
      const res = await fetch(`${API_URL}/payments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Paiement envoyé !");
        onClose();
        closeAllPopups();
      } else {
        toast.error(data.message || "Erreur");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    }
  };

  // === NAVIGATION POPUP ===
  const openMethodList = () => setPopupState("methodList");
  const openMethodDetails = (method) => {
    setSelectedMethodDetails(methodDetails[method.value]);
    setSelectedMethod(method.value);
    setIsCopied(false);
    setShowQR(false);
    setPopupState("methodDetails");
  };
  const handleBack = () => {
    if (popupState === "methodDetails") setPopupState("methodList");
    else if (popupState === "methodList") setPopupState(null);
    else if (popupState === "facilityAmount") setPopupState("facilityPeriods");
    else if (popupState === "facilityPeriods") setPopupState("paymentType");
    else setPopupState(null);
  };

  const handleSingleCheckboxChange = (e, value) => {
    setPaymentType(value);
    if (value === "facility") setPopupState("facilityPeriods");
  };

  if (!isOpen) return null;

  return (
    <div className="details-container-paymentsouspacks">
      {loading ? (
        <p style={{ justifyContent: "center", cursor: "default" }}>Chargement…</p>
      ) : error ? (
        <p style={{ justifyContent: "center", color: "#f09595", cursor: "default" }}>{error}</p>
      ) : (
        <>
          {/* ── HEADER PACK ── */}
          <div className="pay-header">
            <img
              src={`/images/packs/unlimitedBonus/${packName.replace(" ", "_")}.jpg`}
              alt={packName}
              className="pay-header-thumb"
              onError={(e) => (e.target.src = "/images/packs/unlimitedBonus/pack_N.jpg")}
            />
            <div className="pay-header-info">
              <div className="pay-header-name">{packName}</div>
              <div className="pay-header-sub">
                {packType === "international" ? "International" : "Local"}
              </div>
            </div>
            <div className="pay-header-price">{formatPrice(details?.price)} TND</div>
          </div>

          {/* ── QUANTITÉ ── */}
          <div className="pay-section-label">Quantité</div>
          <div className="pay-qty-row">
            <span className="pay-qty-label">Nombre de packs</span>
            <div
              className={`pay-qty-pill ${quantity === 1 ? "active" : "inactive"}`}
              onClick={() => setQuantity(1)}
            >1</div>
            <div
              className={`pay-qty-pill ${quantity === 2 ? "active" : "inactive"}`}
              onClick={() => setQuantity(2)}
            >2</div>
            <span className="pay-qty-total">
              = <span>{formatPrice(totalPrice)} TND</span>
            </span>
          </div>

          {/* ── PAIEMENT ── */}
          <div className="pay-section-label">Paiement</div>

          {/* Type de paiement */}
          <p onClick={() => setPopupState("paymentType")}>
            <div className="pay-row-icon amber">
              <img src="/images/payment/type.jpg" alt="" className="icon1"
                onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="pay-row-text">
              <div className="pay-row-title">Type de paiement</div>
              <div className="pay-row-sub">
                {paymentType ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1) : "Non sélectionné"}
              </div>
            </div>
            <ChevronRight size={14} className="pay-row-arrow" />
          </p>

          {/* Méthode */}
          <p onClick={openMethodList}>
            <div className="pay-row-icon purple">
              <img src="/images/payment/methode.jpg" alt="" className="icon1"
                onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="pay-row-text">
              <div className="pay-row-title">Méthode de paiement</div>
              <div className="pay-row-sub">
                {selectedMethod ? selectedMethod.toUpperCase() : "Non sélectionnée"}
              </div>
            </div>
            {selectedMethod ? (
              <span className="pay-method-badge">{selectedMethod.toUpperCase()}</span>
            ) : (
              <ChevronRight size={14} className="pay-row-arrow" />
            )}
          </p>

          {/* ── REÇU ── */}
          <div className="pay-section-label">Reçu</div>
          <label className={`pay-upload-zone ${receiptPhoto ? "has-file" : ""}`}>
            <div className="pay-upload-icon">
              <Upload size={16} color={receiptPhoto ? "#ffb84d" : "rgba(200,200,215,0.5)"} />
            </div>
            <div className="pay-upload-info">
              <div className="pay-upload-title">{receiptName}</div>
              {!receiptPhoto && (
                <div className="pay-upload-hint">JPG / PNG · max 5 Mo</div>
              )}
            </div>
            <ChevronRight size={14} className="pay-upload-arrow" />
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>

          {/* ── CTA ── */}
          <div className="packFooter">
            <button className="pay-submit-btn" onClick={handleSubmit}>
              <Send size={14} />
              Soumettre le paiement
            </button>
          </div>

          {/* ── POPUP / BOTTOM SHEET ── */}
          {popupState && (
            <div className="infoOverlaysouspacks" onClick={handleBack}>
              <div className="infoModalsouspacks" onClick={(e) => e.stopPropagation()}>
                <ArrowLeft className="close_icon" onClick={handleBack} />

                {/* LISTE MÉTHODES */}
                {popupState === "methodList" && (
                  <>
                    {/* Onglets Local / International */}
                    <div className="pay-toggle-tabs">
                      <button
                        className={`pay-tab ${activeTab === "local" ? "active" : ""}`}
                        onClick={() => setActiveTab("local")}
                      >Local</button>
                      <button
                        className={`pay-tab ${activeTab === "international" ? "active" : ""}`}
                        onClick={() => setActiveTab("international")}
                      >International</button>
                    </div>
                    <div className="method-grid">
                      {availableMethods.map((method) => (
                        <p
                          key={method.value}
                          className={selectedMethod === method.value ? "selected" : ""}
                          onClick={() => openMethodDetails(method)}
                        >
                          <img
                            src={`/images/payment/${method.image}`}
                            alt={method.label}
                            className="icon1"
                          />
                          {method.label}
                        </p>
                      ))}
                    </div>
                  </>
                )}

                {/* DÉTAILS MÉTHODE */}
                {popupState === "methodDetails" && selectedMethodDetails && (
                  <div className="simple-details">
                    <div className="simple-line">
                      <span className="simple-title">Agency</span>
                      <span className="simple-info rib-line">{selectedMethodDetails.agency}</span>
                    </div>
                    <div className="simple-line">
                      <span className="simple-title">RIB</span>
                      <span className="simple-info rib-line">
                        <span className="rib-text">{selectedMethodDetails.number}</span>
                        <span
                          className="copy-icon"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedMethodDetails.number);
                            setIsCopied(true);
                            toast.success("RIB copié !", { autoClose: 1200 });
                            setTimeout(() => setIsCopied(false), 1500);
                          }}
                        >
                          {isCopied ? <Check size={12} /> : <Copy size={12} />}
                        </span>
                      </span>
                    </div>
                    <div className="simple-line">
                      <span className="simple-title">Contact</span>
                      <span className="simple-info rib-line">
                        {selectedMethodDetails.phone || "—"}
                      </span>
                    </div>
                    {selectedMethodDetails.qrCode && (
                      <div className="simple-line qr-line" onClick={() => setShowQR(true)}>
                        <span className="simple-title">QR Code</span>
                        <span className="simple-info qr-hint">Appuyer pour voir</span>
                      </div>
                    )}
                    <div className="simple-footer">
                      <button
                        className="simple-btn"
                        onClick={() => {
                          setPopupState(null);
                          toast.success(`${selectedMethod.toUpperCase()} sélectionné`);
                        }}
                      >
                        Confirmer
                      </button>
                    </div>

                    {/* QR popup */}
                    {showQR && selectedMethodDetails.qrCode && (
                      <div className="simple-overlay" onClick={() => setShowQR(false)}>
                        <div className="simple-modal" onClick={(e) => e.stopPropagation()}>
                          <img
                            src={selectedMethodDetails.qrCode}
                            alt="QR Code"
                            className="simple-qr"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TYPE DE PAIEMENT */}
                {popupState === "paymentType" && (
                  <>
                    {details?.paymentFacilities?.length > 0 && (
                      <p>
                        <img
                          src="/images/payment/facilite.jpg"
                          alt="facilité"
                          className="icon1"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        Paiement facilité
                        <input
                          type="checkbox"
                          value="facility"
                          checked={paymentType === "facility"}
                          onChange={(e) => handleSingleCheckboxChange(e, "facility")}
                        />
                      </p>
                    )}
                    <p>
                      <img
                        src="/images/payment/pay_cache.jpg"
                        alt="cash"
                        className="icon1"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      Paiement cash
                      <input
                        type="checkbox"
                        value="cash"
                        checked={paymentType === "cash"}
                        onChange={(e) => handleSingleCheckboxChange(e, "cash")}
                      />
                    </p>
                  </>
                )}

                {/* PÉRIODES FACILITÉ */}
                {popupState === "facilityPeriods" &&
                  details?.paymentFacilities?.map((f) => (
                    <p key={f.period}>
                      <img
                        src={`/images/payment/${f.period.replace(" ", "")}.jpg`}
                        alt={f.period}
                        className="icon1"
                        onError={(e) => (e.target.src = "/images/payment/date.png")}
                      />
                      Période {f.period}
                      <input
                        type="checkbox"
                        value={f.period}
                        checked={selectedFacility === f.period}
                        onChange={() => {
                          setSelectedFacility(f.period);
                          setPopupState("facilityAmount");
                        }}
                      />
                    </p>
                  ))}

                {/* MONTANT FACILITÉ */}
                {popupState === "facilityAmount" && selectedFacility && (
                  <p style={{ cursor: "default" }}>
                    <img
                      src="/images/payment/calcule.png"
                      alt="calcul"
                      className="icon1"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    {(() => {
                      const f = details.paymentFacilities.find(
                        (g) => g.period === selectedFacility
                      );
                      return f
                        ? `${formatPrice(f.monthlyAmount)} TND × ${f.period} = ${formatPrice(f.totalAmount)} TND`
                        : "";
                    })()}
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Payment;